import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './App.css';
import SocialShare from './components/SocialShare';
import History from './components/History';
import Statistics from './components/Statistics';
import InfoSection from './components/InfoSection';
import { saveToHistory } from './utils/localStorage';
import { formatNumbersForClipboard, copyToClipboard, getShareUrl } from './utils/shareUtils';

function App() {
  const [lottoNumbers, setLottoNumbers] = useState([]);
  const [superzahl, setSuperzahl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [generationCount, setGenerationCount] = useState(0);
  const [multipleSets, setMultipleSets] = useState([]);

  // Load numbers from URL parameters if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const numbersParam = params.get('numbers');
    const superzahlParam = params.get('superzahl');
    
    if (numbersParam && superzahlParam) {
      const numbers = numbersParam.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n) && n >= 1 && n <= 49);
      const sz = parseInt(superzahlParam);
      if (numbers.length === 6 && !isNaN(sz) && sz >= 0 && sz <= 9) {
        setLottoNumbers(numbers);
        setSuperzahl(sz);
      }
    }
  }, []);

  // Helper function to generate a single set of lotto numbers
  const generateSingleSet = () => {
    const numbers = [];
    while (numbers.length < 6) {
      const random = Math.floor(Math.random() * 49) + 1;
      if (!numbers.includes(random)) {
        numbers.push(random);
      }
    }
    numbers.sort((a, b) => a - b);
    const superzahl = Math.floor(Math.random() * 10);
    
    return { numbers, superzahl };
  };

  const generateLottoNumbers = (count = 1) => {
    setIsGenerating(true);
    
    // Clear multiple sets when generating a single set
    setMultipleSets([]);
    
    // Add animation delay
    setTimeout(() => {
      const { numbers, superzahl: superzahlRandom } = generateSingleSet();
      
      setLottoNumbers(numbers);
      setSuperzahl(superzahlRandom);
      setIsGenerating(false);
      setGenerationCount(prev => prev + 1);
      
      // Save to history
      saveToHistory(numbers, superzahlRandom);
      
      // Track generation event
      if (window.gtag) {
        window.gtag('event', 'generate_numbers', {
          event_category: 'engagement',
          event_label: 'Number Generation'
        });
      }
    }, 300);
  };

  const generateMultipleSets = () => {
    setIsGenerating(true);
    
    setTimeout(() => {
      const sets = [];
      for (let i = 0; i < 5; i++) {
        const set = generateSingleSet();
        sets.push(set);
        
        // Save each set to history
        saveToHistory(set.numbers, set.superzahl);
        setGenerationCount(prev => prev + 1);
      }
      
      setMultipleSets(sets);
      setIsGenerating(false);
      
      // Track generation event
      if (window.gtag) {
        window.gtag('event', 'generate_multiple_sets', {
          event_category: 'engagement',
          event_label: '5 Sets Generation'
        });
      }
    }, 300);
  };

  const handleSelectSet = (numbers, sz) => {
    setLottoNumbers([...numbers]);
    setSuperzahl(sz);
    setMultipleSets([]);
  };

  const handleCopyToClipboard = async () => {
    if (lottoNumbers.length === 0) return;
    
    const text = formatNumbersForClipboard(lottoNumbers, superzahl);
    const success = await copyToClipboard(text);
    
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
      
      // Track copy event
      if (window.gtag) {
        window.gtag('event', 'copy_numbers', {
          event_category: 'engagement',
          event_label: 'Copy to Clipboard'
        });
      }
    }
  };

  const handleSelectFromHistory = (numbers, sz) => {
    setLottoNumbers([...numbers]);
    setSuperzahl(sz);
    setMultipleSets([]); // Clear multiple sets when selecting from history
    setShowHistory(false);
  };

  const renderLottoNumbers = lottoNumbers.map((number, index) => (
    <div 
      className={`lotto-ball ${isGenerating ? 'generating' : ''}`} 
      key={index}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {number}
    </div>
  ));

  const superzahlBall = superzahl !== null ? (
    <div className={`superzahl-ball ${isGenerating ? 'generating' : ''}`}>
      {superzahl}
    </div>
  ) : null;

  const shareUrl = lottoNumbers.length > 0 ? getShareUrl(lottoNumbers, superzahl) : 'https://lottozahlen-generator.net/';
  const shareText = lottoNumbers.length > 0 
    ? formatNumbersForClipboard(lottoNumbers, superzahl)
    : 'Kostenloser Lottozahlen Generator für 6 aus 49 mit Superzahl';

  return (
    <div className="App">
      <Helmet>
        <title>
          {lottoNumbers.length > 0 
            ? `Meine Lottozahlen: ${lottoNumbers.join(', ')} + Superzahl: ${superzahl} | Lottozahlen Generator`
            : 'Lottozahlen Generator | Kostenlose 6 aus 49 Zahlen mit Superzahl'
          }
        </title>
        <meta name="description" content={shareText} />
        <meta property="og:title" content={lottoNumbers.length > 0 ? `Meine Lottozahlen: ${lottoNumbers.join(', ')}` : 'Lottozahlen Generator'} />
        <meta property="og:description" content={shareText} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:title" content={lottoNumbers.length > 0 ? `Meine Lottozahlen: ${lottoNumbers.join(', ')}` : 'Lottozahlen Generator'} />
        <meta name="twitter:description" content={shareText} />
        <link rel="canonical" href={shareUrl} />
      </Helmet>
      <div className="App-header min-vh-100 d-flex flex-column align-items-center justify-content-start text-white py-5">
        <div className="container">
          <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
            <img 
              src={`${process.env.PUBLIC_URL}/logo512.png`} 
              alt="Lottozahlen Generator Logo" 
              style={{ width: '64px', height: '64px', objectFit: 'contain' }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h1 className="display-4 fw-bold mb-0 text-center">Lottozahlen Generator</h1>
          </div>
          <p className="lead text-center mb-5">Generiere deine Lottozahlen für 6 aus 49 und teste dein Glück!</p>
          
          <div className="row justify-content-center">
            <div className="col-12 col-lg-8 col-xl-6 mb-4">
              <div className="card bg-light bg-opacity-10 border-0 shadow-lg">
                <div className="card-body p-4">
                  {multipleSets.length > 0 ? (
                    <>
                      <h5 className="card-title text-white mb-3 text-center">5 Sets generiert:</h5>
                      <div className="row g-3 mb-4">
                        {multipleSets.map((set, setIndex) => (
                          <div key={setIndex} className="col-12 col-md-6 col-lg-4">
                            <div 
                              className="card bg-dark bg-opacity-25 border border-light border-opacity-25 p-3 cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleSelectSet(set.numbers, set.superzahl)}
                            >
                              <div className="text-center mb-2">
                                <small className="text-muted">Set {setIndex + 1}</small>
                              </div>
                              <div className="d-flex justify-content-center flex-wrap gap-2 mb-2">
                                {set.numbers.map((number, index) => (
                                  <div key={index} className="lotto-ball" style={{ fontSize: '0.9rem', width: '2.5rem', height: '2.5rem', lineHeight: '2.5rem' }}>
                                    {number}
                                  </div>
                                ))}
                              </div>
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                <small className="text-white">Superzahl:</small>
                                <div className="superzahl-ball" style={{ fontSize: '0.9rem', width: '2rem', height: '2rem', lineHeight: '2rem' }}>
                                  {set.superzahl}
                                </div>
                              </div>
                              <div className="text-center mt-2">
                                <small className="text-white fw-semibold">Klicken zum Auswählen</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : lottoNumbers.length > 0 && (
                    <>
                      <h5 className="card-title text-white mb-3">Deine Lottozahlen:</h5>
                      <div className="d-flex justify-content-center flex-wrap gap-3 mb-4">
                        {renderLottoNumbers}
                      </div>
                      <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
                        <h5 className="text-white mb-0">Deine Superzahl:</h5>
                        {superzahlBall}
                      </div>
                    </>
                  )}

                  <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center mb-3">
                    <button 
                      className="btn btn-primary btn-lg px-4" 
                      onClick={() => generateLottoNumbers()}
                      disabled={isGenerating}
                    >
                      {isGenerating ? 'Generiere...' : 'Ziehe meine Glückszahlen!'}
                    </button>
                    <button 
                      className="btn btn-success btn-lg px-4" 
                      onClick={generateMultipleSets}
                      disabled={isGenerating}
                    >
                      5 Sets generieren
                    </button>
                  </div>

                  {multipleSets.length === 0 && lottoNumbers.length > 0 && (
                    <div className="d-flex justify-content-center">
                      <button 
                        className={`btn ${copySuccess ? 'btn-success' : 'btn-outline-light'}`}
                        onClick={handleCopyToClipboard}
                      >
                        {copySuccess ? '✓ Kopiert!' : '📋 Kopieren'}
                      </button>
                    </div>
                  )}

                  {multipleSets.length === 0 && lottoNumbers.length > 0 && <SocialShare numbers={lottoNumbers} superzahl={superzahl} />}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-10">
              <div className="btn-group w-100 mb-4" role="group">
                <button
                  type="button"
                  className={`btn ${showHistory ? 'btn-warning' : 'btn-outline-light'}`}
                  onClick={() => {
                    setShowHistory(!showHistory);
                    setShowStatistics(false);
                    setShowInfo(false);
                  }}
                >
                  📜 Verlauf
                </button>
                <button
                  type="button"
                  className={`btn ${showStatistics ? 'btn-warning' : 'btn-outline-light'}`}
                  onClick={() => {
                    setShowStatistics(!showStatistics);
                    setShowHistory(false);
                    setShowInfo(false);
                  }}
                >
                  📊 Statistiken
                </button>
                <button
                  type="button"
                  className={`btn ${showInfo ? 'btn-warning' : 'btn-outline-light'}`}
                  onClick={() => {
                    setShowInfo(!showInfo);
                    setShowHistory(false);
                    setShowStatistics(false);
                  }}
                >
                  ℹ️ Info
                </button>
              </div>

              {showHistory && (
                <div className="fade-in">
                  <History onSelectNumbers={handleSelectFromHistory} refreshTrigger={generationCount} />
                </div>
              )}

              {showStatistics && (
                <div className="fade-in">
                  <Statistics />
                </div>
              )}

              {showInfo && (
                <div className="fade-in">
                  <InfoSection />
                </div>
              )}
            </div>
          </div>
        </div>
        
        <footer className="mt-5 py-4 text-center text-white-50">
          <div className="container">
            <p className="mb-1">© {new Date().getFullYear()} Lottozahlen Generator</p>
            <p className="mb-0 small">Kostenloser Generator für 6 aus 49 mit Superzahl</p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;
